/**
 * InspirationBoard — the Pinterest-like board (docs/INSPIRATION_BOARD.md).
 *
 * ONE component for both sides (one-component-per-node-type rule, applied to the board):
 *   - mode 'client' — inside the Client Hub (JourneyPage), scope { token }. Fixed light palette so
 *     it reads the same for every client regardless of the app's theme.
 *   - mode 'team'   — opened from the outline (Reference > Design > Client Inspiration), scope
 *     { projectId }. Themed through the app's --aj-* tokens, so dark mode works.
 * The server decides what each side may do and filters team-only notes; this component only
 * hides controls the server would refuse anyway.
 *
 * Polish pass (2026-10-13, Matt: "a luxury product, not Windows 98"): no native selects,
 * checkboxes or browser-styled controls anywhere. Every control is one of the primitives at the
 * bottom of this file (Button, IconButton, Field, Switch, Popover, BoardPicker, Menu) styled by the
 * scoped stylesheet in CSS, so hover, press and keyboard focus states are consistent. The board
 * picker is a searchable combobox that can also create a board from what was typed.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import SortableMasonry, { SORT_CSS } from './SortableMasonry.jsx'
import {
  getBoard, moveItem, uploadImages, pictureFiles, addLink, updateItem, deleteItem, addComment, deleteComment,
  createCollection, renameCollection, deleteCollection,
} from '../../services/inspiration.js'

const CLIENT_VARS = {
  '--ib-bg': '#f6f4f0', '--ib-surface': '#ffffff', '--ib-subtle': '#f1eee9', '--ib-hover': 'rgba(7,39,45,0.05)',
  '--ib-text': '#07272D', '--ib-mid': '#4a6166', '--ib-muted': '#8a9a9c', '--ib-border': 'rgba(7,39,45,0.10)',
  '--ib-border-strong': 'rgba(7,39,45,0.22)', '--ib-accent': '#07272D', '--ib-on-accent': '#ffffff',
  '--ib-ring': 'rgba(7,39,45,0.18)', '--ib-gold': '#b08d57',
}
const TEAM_VARS = {
  '--ib-bg': 'var(--aj-bg-page, #f4f7f8)', '--ib-surface': 'var(--aj-bg-surface, #ffffff)', '--ib-subtle': 'var(--aj-bg-subtle, #eef2f3)',
  '--ib-hover': 'var(--aj-bg-muted, rgba(7,39,45,0.05))',
  '--ib-text': 'var(--aj-text, #07272D)', '--ib-mid': 'var(--aj-text-mid, #3a5a60)', '--ib-muted': 'var(--aj-text-muted, #7a9aaa)',
  '--ib-border': 'var(--aj-border, rgba(7,39,45,0.12))', '--ib-border-strong': 'var(--aj-border-mid, rgba(7,39,45,0.22))',
  '--ib-accent': 'var(--aj-text, #07272D)', '--ib-on-accent': 'var(--aj-bg-surface, #ffffff)',
  '--ib-ring': 'rgba(120,150,160,0.35)', '--ib-gold': '#b08d57',
}
const HEART = '#c2414f'
const PIN_MIME = 'application/x-aj-inspiration-pin'

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })
}
function hostOf(url) { try { return new URL(url).hostname.replace(/^www\./, '') } catch { return '' } }
function initials(name) {
  const p = String(name || '').trim().split(/\s+/).filter(Boolean)
  return ((p[0] || '?')[0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase()
}

/* ── Icons (stroke icons, inherit currentColor) ─────────────────────────────── */
function Icon({ d, size = 16, fill = 'none', sw = 1.6, children }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={sw}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {d ? <path d={d} /> : children}
    </svg>
  )
}
const I = {
  heart: (p) => <Icon {...p} d="M12 20.5s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.7c0 5.6-7.5 10.2-7.5 10.2z" />,
  close: (p) => <Icon {...p} d="M6 6l12 12M18 6L6 18" />,
  left: (p) => <Icon {...p} d="M15 5l-7 7 7 7" />,
  right: (p) => <Icon {...p} d="M9 5l7 7-7 7" />,
  down: (p) => <Icon {...p} d="M6 9l6 6 6-6" />,
  check: (p) => <Icon {...p} d="M5 12.5l4.5 4.5L19 7.5" />,
  search: (p) => <Icon {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></Icon>,
  plus: (p) => <Icon {...p} d="M12 5v14M5 12h14" />,
  upload: (p) => <Icon {...p} d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />,
  link: (p) => <Icon {...p} d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  chat: (p) => <Icon {...p} d="M5 18.5V6.5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 2z" />,
  more: (p) => <Icon {...p}><circle cx="5.5" cy="12" r="1.2" fill="currentColor" /><circle cx="12" cy="12" r="1.2" fill="currentColor" /><circle cx="18.5" cy="12" r="1.2" fill="currentColor" /></Icon>,
  pencil: (p) => <Icon {...p} d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />,
  trash: (p) => <Icon {...p} d="M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12" />,
  external: (p) => <Icon {...p} d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />,
  send: (p) => <Icon {...p} d="M5 12l14-7-5 14-2.5-5.5L5 12z" />,
  folder: (p) => <Icon {...p} d="M4 7a2 2 0 0 1 2-2h4l2 2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z" />,
  inbox: (p) => <Icon {...p} d="M4 13l2.5-7h11l2.5 7v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-5zM4 13h5l1 2h4l1-2h5" />,
  lock: (p) => <Icon {...p}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Icon>,
  image: (p) => <Icon {...p}><rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M20 16l-5-5-8 8" /></Icon>,
}

/* ── Board ──────────────────────────────────────────────────────────────────── */

export default function InspirationBoard({ scope, mode = 'client', projectName, initialTab = 'all', onClose, onAuthError, closeLabel = 'Close' }) {
  const isTeam = mode === 'team'
  const [board, setBoard] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [tab, setTab] = useState(initialTab)
  const [detailId, setDetailId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(null)    // { done, total } while uploading
  const [toast, setToast] = useState(null)          // { text, tone: 'ok'|'err' }
  const [linkOpen, setLinkOpen] = useState(false)
  const [fileDrag, setFileDrag] = useState(false)
  const [dropTab, setDropTab] = useState(null)
  const fileRef = useRef(null)
  const toastTimer = useRef(null)
  const scopeKey = scope.token || scope.projectId

  useEffect(() => {
    let alive = true
    setBoard(null); setLoadError(null)
    getBoard(scope).then(b => { if (alive) setBoard(b) }).catch(e => {
      if (!alive) return
      // The standalone client page shows its own access-code / dead-link screens for these.
      if (onAuthError && (e.code === 'pin_required' || e.code === 'not_found' || e.code === 'not_yet')) onAuthError(e.code)
      else setLoadError(e.message)
    })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev; clearTimeout(toastTimer.current) }
  }, [])

  function say(text, tone = 'ok') {
    clearTimeout(toastTimer.current)
    setToast({ text, tone })
    if (tone !== 'info') toastTimer.current = setTimeout(() => setToast(null), tone === 'err' ? 7000 : 2400)
  }

  async function run(fn, okText) {
    setBusy(true)
    try {
      const b = await fn()
      if (b && b.items) setBoard(b)
      if (okText) say(okText)
      return b
    } catch (e) { say(e.message || 'Something went wrong. Please try again.', 'err'); return null }
    finally { setBusy(false) }
  }

  // The board's shared order (database/299): position ascending, newest first among unplaced rows.
  const items = useMemo(() => (board?.items || []).slice().sort((a, b) => {
    const pa = a.position == null ? -Infinity : a.position, pb = b.position == null ? -Infinity : b.position
    return pa !== pb ? pa - pb : String(b.createdAt).localeCompare(String(a.createdAt))
  }), [board])
  const scrollRef = useRef(null)

  // Drag to rearrange: show the new order at once, then save it for everyone.
  async function rearrange(id, prevId, nextId) {
    setBoard(b => {
      if (!b) return b
      const pos = Object.fromEntries(b.items.map(i => [i.id, i.position]))
      const p = prevId ? pos[prevId] : null, n = nextId ? pos[nextId] : null
      const np = (p != null && n != null) ? (p + n) / 2 : p != null ? p + 1 : n != null ? n - 1 : 0
      return { ...b, items: b.items.map(i => i.id === id ? { ...i, position: np } : i) }
    })
    await run(() => moveItem(scope, id, prevId, nextId))
  }

  // A picture dragged onto a board tab is filed there instead of rearranged.
  function fileTo(id, key) {
    if (key === 'fav') return run(() => updateItem(scope, id, { favorite: true }), 'Added to favorites')
    if (key === 'unsorted') return run(() => updateItem(scope, id, { collectionId: null }), 'Moved to Unsorted')
    const c = collections.find(x => x.id === key)
    if (c) return run(() => updateItem(scope, id, { collectionId: key }), 'Moved to ' + c.name)
  }
  const collections = board?.collections || []
  const realCollectionId = collections.some(c => c.id === tab) ? tab : null

  const visible = useMemo(() => {
    if (tab === 'all') return items
    if (tab === 'fav') return items.filter(i => i.favorite)
    if (tab === 'unsorted') return items.filter(i => !i.collectionId)
    return items.filter(i => i.collectionId === tab)
  }, [items, tab])

  const counts = useMemo(() => {
    const m = { all: items.length, fav: 0, unsorted: 0 }
    for (const i of items) {
      if (i.favorite) m.fav++
      if (!i.collectionId) m.unsorted++
      else m[i.collectionId] = (m[i.collectionId] || 0) + 1
    }
    return m
  }, [items])

  // A cover picture per board, for the picker.
  const covers = useMemo(() => {
    const m = {}
    for (const i of items) if (i.collectionId && !m[i.collectionId] && i.imageUrl) m[i.collectionId] = i.imageUrl
    return m
  }, [items])

  useEffect(() => {
    if (board && !['all', 'fav', 'unsorted'].includes(tab) && !collections.some(c => c.id === tab)) setTab('all')
  }, [board, tab, collections])

  async function doUpload(files) {
    // Reads each file's own bytes: phone photos from Files or Dropbox often arrive with no type.
    const list = await pictureFiles(files)
    if (!list.length) { say('Only pictures can be added here. To save something from a website, use Add link.', 'err'); return }
    setProgress({ done: 0, total: list.length })
    const b = await run(() => uploadImages(scope, list, { collectionId: realCollectionId, onProgress: (done, total) => setProgress({ done, total }) }))
    setProgress(null)
    if (!b) return
    const added = list.length - (b.skipped || 0)
    if (b.skipped) {
      say((added ? added + ' added. ' : '') + (b.skipped === 1 ? 'One photo' : b.skipped + ' photos') +
        ' couldn\u2019t be opened in this browser (Apple\u2019s HEIC format). Open the board in Safari, or save the photo as a JPEG first.', 'err')
    } else say(added === 1 ? 'Picture added' : added + ' pictures added')
  }

  // A web address pasted or dropped anywhere on the board is saved like Add link: the server finds
  // the picture and keeps its own copy. `sourceUrl` is the page a dragged picture came from.
  async function addFromUrls(urls, sourceUrl) {
    const list = urls.slice(0, 10)
    for (let i = 0; i < list.length; i++) {
      say(list.length > 1 ? `Saving picture ${i + 1} of ${list.length}\u2026` : 'Saving the picture from that page\u2026', 'info')
      await run(() => addLink(scope, list[i], { collectionId: realCollectionId, sourceUrl }),
        i === list.length - 1 ? (list.length > 1 ? list.length + ' pictures saved' : 'Picture saved to the board') : null)
    }
  }

  async function addDataUrl(dataUrl) {
    try {
      const blob = await (await fetch(dataUrl)).blob()
      if (!/^image\//.test(blob.type)) throw new Error('not an image')
      await doUpload([new File([blob], 'picture.' + (blob.type.split('/')[1] || 'png'), { type: blob.type })])
    } catch { say('We couldn\u2019t read that picture. Save it and add it here instead.', 'err') }
  }

  // Whatever was dropped or pasted: picture files first, then a picture inside dropped web content
  // (dragging an image from another tab), then plain web addresses.
  function takeTransfer(dt) {
    const files = Array.from(dt.files || []).filter(f => /^image\//.test(f.type) || !f.type)
    if (files.length) { doUpload(files); return true }
    const html = dt.getData && dt.getData('text/html')
    const page = (dt.getData && (dt.getData('text/uri-list') || '').split(/\r?\n/).find(l => l && !l.startsWith('#'))) || null
    const imgSrc = html && ((html.match(/<img[^>]+src=["']([^"']+)["']/i) || [])[1] || '').replace(/&amp;/g, '&')
    if (imgSrc && /^data:image\//i.test(imgSrc)) { addDataUrl(imgSrc); return true }
    if (imgSrc && /^https?:\/\//i.test(imgSrc)) {
      addFromUrls([imgSrc], page && page !== imgSrc && /^https?:/i.test(page) ? page : null); return true
    }
    const text = (dt.getData && dt.getData('text/plain')) || page || ''
    const urls = extractUrls(text)
    if (urls.length) { addFromUrls(urls); return true }
    if (/^data:image\//i.test(text.trim())) { addDataUrl(text.trim()); return true }
    return false
  }

  // Picture links are private and expire after a few hours. A board left open that long would show
  // blank cards; when a picture fails to load, fetch fresh links once (at most once a minute).
  const lastRefresh = useRef(0)
  function refreshLinks() {
    if (Date.now() - lastRefresh.current < 60000) return
    lastRefresh.current = Date.now()
    getBoard(scope).then(b => b && b.items && setBoard(b)).catch(() => {})
  }

  async function makeBoard(name) {
    const b = await run(() => createCollection(scope, name), 'Board created')
    return b && b.createdId ? b.createdId : null
  }

  useEffect(() => {
    function onPaste(e) {
      if (!e.clipboardData || detailId) return
      const tag = (document.activeElement && document.activeElement.tagName) || ''
      const typing = tag === 'INPUT' || tag === 'TEXTAREA'
      const files = Array.from(e.clipboardData.files || []).filter(f => /^image\//.test(f.type))
      // A picture pasted while the Add link window is open goes straight on the board.
      if (files.length && (!typing || linkOpen)) { e.preventDefault(); setLinkOpen(false); doUpload(files); return }
      if (typing || linkOpen) return
      if (takeTransfer(e.clipboardData)) e.preventDefault()
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  })

  // Escape closes the innermost layer. Popovers and menus catch Escape first (capture phase).
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return
      if (detailId) setDetailId(null)
      else if (linkOpen) setLinkOpen(false)
      else onClose && onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [detailId, linkOpen, onClose])

  // Files from the computer, or pictures and links dragged from another website. Our own pins
  // (dragged onto a tab to file them) are left to the tabs.
  function isAddable(dt) {
    const types = Array.from(dt.types || [])
    if (types.includes(PIN_MIME)) return false
    return types.includes('Files') || types.includes('text/uri-list') || types.includes('text/html')
  }
  function onBoardDragOver(e) {
    if (isAddable(e.dataTransfer)) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setFileDrag(true) }
  }
  function onBoardDrop(e) {
    setFileDrag(false)
    if (!isAddable(e.dataTransfer)) return
    e.preventDefault()
    if (!takeTransfer(e.dataTransfer)) say('That can\u2019t be added. Drop a picture, or a link to one.', 'err')
  }
  // Board tabs are drop targets for a lifted picture (SortableMasonry reads data-drop-key).
  function tabDragProps(key) {
    return { dropping: dropTab === key, dropKey: key === 'all' || key === tab ? null : key }
  }

  const detail = detailId ? items.find(i => i.id === detailId) : null
  const detailIdx = detail ? visible.findIndex(i => i.id === detail.id) : -1
  const activeCollection = collections.find(c => c.id === tab)
  const canEditCollection = !!activeCollection && (isTeam || activeCollection.createdByKind === 'client')

  return (
    <div className="ib-root" style={{ ...S.root, ...(isTeam ? TEAM_VARS : CLIENT_VARS) }}
      onDragOver={onBoardDragOver} onDragLeave={e => { if (e.currentTarget === e.target) setFileDrag(false) }} onDrop={onBoardDrop}>
      <style>{CSS + SORT_CSS}</style>

      {progress && <div className="ib-progress"><div style={{ width: Math.max(6, Math.round(100 * progress.done / progress.total)) + '%' }} /></div>}

      <header style={S.header}>
        <div style={{ minWidth: 0 }}>
          <div style={S.eyebrow}>{projectName || board?.projectName || 'Alder James'}</div>
          <h1 style={S.title}>Inspiration</h1>
        </div>
        <div style={S.headerActions}>
          <Button variant="primary" icon={I.upload} onClick={() => fileRef.current && fileRef.current.click()} disabled={!!progress}>
            {progress ? `Adding ${progress.done} of ${progress.total}` : 'Add pictures'}
          </Button>
          <Button variant="ghost" icon={I.link} onClick={() => setLinkOpen(true)}>Add link</Button>
          <IconButton label={closeLabel} onClick={onClose} size={40}><I.close size={18} /></IconButton>
        </div>
        <div className="ib-hint">Drag pictures to rearrange them. You can also drag in or paste pictures and links from anywhere, like Pinterest or a screenshot.</div>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden
          onChange={e => {
            // Copy before clearing: the FileList is live, and clearing the input empties it (iPhone
            // Safari), which made every pick read as "no pictures".
            const f = Array.from(e.target.files || []); e.target.value = ''; doUpload(f)
          }} />
      </header>

      <nav className="ib-tabs" style={S.tabsRow} aria-label="Boards">
        <Tab active={tab === 'all'} label="All" count={counts.all} onClick={() => setTab('all')} {...tabDragProps('all')} />
        <Tab active={tab === 'fav'} label="Favorites" icon={I.heart} count={counts.fav} onClick={() => setTab('fav')} {...tabDragProps('fav')} />
        {counts.unsorted > 0 && collections.length > 0 && (
          <Tab active={tab === 'unsorted'} label="Unsorted" count={counts.unsorted} onClick={() => setTab('unsorted')} {...tabDragProps('unsorted')} />
        )}
        {collections.length > 0 && <span style={S.tabDivider} />}
        {collections.map(c => (
          <Tab key={c.id} active={tab === c.id} label={c.name} count={counts[c.id] || 0} onClick={() => setTab(c.id)} {...tabDragProps(c.id)}
            menu={tab === c.id && (isTeam || c.createdByKind === 'client') ? (
              <BoardMenu collection={c}
                onRename={name => run(() => renameCollection(scope, c.id, name), 'Board renamed')}
                onDelete={() => run(() => deleteCollection(scope, c.id), 'Board deleted')} />
            ) : null} />
        ))}
        <NewBoardButton onCreate={async name => { const id = await makeBoard(name); if (id) setTab(id) }} />
      </nav>

      {activeCollection && !canEditCollection && <div style={S.boardNote}>Board started by Alder James</div>}

      <main style={S.scroll} ref={scrollRef}>
        {!board && !loadError && <SkeletonGrid />}
        {loadError && (
          <div style={S.emptyHero}>
            <div style={S.emptyTitle}>We couldn&rsquo;t open the board</div>
            <div style={S.emptyBody}>{loadError}</div>
            <Button variant="ghost" onClick={() => window.location.reload()}>Try again</Button>
          </div>
        )}
        {board && visible.length === 0 && (
          <EmptyState tab={tab} hasAny={items.length > 0} isTeam={isTeam} boardName={activeCollection?.name}
            onPick={() => fileRef.current && fileRef.current.click()} onLink={() => setLinkOpen(true)} />
        )}
        {visible.length > 0 && (
          <SortableMasonry items={visible} scrollRef={scrollRef}
            onOpen={id => setDetailId(id)}
            onReorder={(id, prevId, nextId) => rearrange(id, prevId, nextId)}
            onDropTarget={fileTo}
            onHoverTarget={setDropTab}
            renderItem={it => (
              <PinCard item={it} collections={collections} showCollection={tab === 'all' || tab === 'fav'}
                onOpen={() => setDetailId(it.id)} onImageError={refreshLinks}
                onHeart={() => run(() => updateItem(scope, it.id, { favorite: !it.favorite }))} />
            )} />
        )}
      </main>

      {fileDrag && (
        <div className="ib-dropveil">
          <div className="ib-dropcard">
            <I.image size={28} />
            <div style={S.dropTitle}>Drop to add</div>
            <div style={S.dropSub}>{activeCollection ? 'They will go on ' + activeCollection.name : 'Release anywhere on the board'}</div>
          </div>
        </div>
      )}

      {linkOpen && (
        <LinkDialog collections={collections} covers={covers} counts={counts} initialBoard={realCollectionId}
          onCreateBoard={makeBoard}
          onFiles={files => { setLinkOpen(false); doUpload(files) }}
          onCancel={() => setLinkOpen(false)}
          onSave={async (url, note, collectionId) => {
            say('Saving the picture from that page\u2026', 'info')
            const b = await run(() => addLink(scope, url, { note, collectionId }), 'Picture saved to the board')
            if (b) setLinkOpen(false)
            return !!b
          }} />
      )}

      {detail && (
        <PinDetail item={detail} isTeam={isTeam} collections={collections} covers={covers} counts={counts} busy={busy} onImageError={refreshLinks}
          position={detailIdx >= 0 ? (detailIdx + 1) + ' of ' + visible.length : ''}
          hasPrev={detailIdx > 0} hasNext={detailIdx >= 0 && detailIdx < visible.length - 1}
          onPrev={() => setDetailId(visible[detailIdx - 1].id)} onNext={() => setDetailId(visible[detailIdx + 1].id)}
          onClose={() => setDetailId(null)}
          onCreateBoard={makeBoard}
          onUpdate={(patch, okText) => run(() => updateItem(scope, detail.id, patch), okText)}
          onDelete={async () => { setDetailId(null); await run(() => deleteItem(scope, detail.id), 'Picture removed') }}
          onComment={(body, internal) => run(() => addComment(scope, detail.id, body, internal))}
          onDeleteComment={id => run(() => deleteComment(scope, id), 'Comment removed')} />
      )}

      {toast && <div className={'ib-toast' + (toast.tone === 'err' ? ' ib-toast--err' : '')} role="status">{toast.text}</div>}
    </div>
  )
}

/* ── Tabs and board management ─────────────────────────────────────────────── */

function Tab({ active, label, icon: Ic, count, onClick, dropping, dropKey, menu }) {
  return (
    <div className={'ib-tab' + (active ? ' is-active' : '') + (dropping ? ' is-drop' : '')} data-drop-key={dropKey || undefined}>
      <button className="ib-tab-main" onClick={onClick} aria-pressed={active}>
        {Ic && <Ic size={13} sw={1.8} />}
        <span className="ib-tab-label">{label}</span>
        {count != null && <span className="ib-tab-count">{count}</span>}
      </button>
      {menu}
    </div>
  )
}

function NewBoardButton({ onCreate }) {
  const [open, setOpen] = useState(false)
  const anchor = useRef(null)
  return (
    <>
      <button ref={anchor} className="ib-tab ib-tab--new" onClick={() => setOpen(o => !o)}>
        <I.plus size={13} sw={1.8} /><span>New board</span>
      </button>
      <Popover anchor={anchor} open={open} onClose={() => setOpen(false)} width={300}>
        <NameForm title="New board" placeholder="e.g. Kitchen, Exterior, Primary bath" cta="Create board"
          onCancel={() => setOpen(false)} onSave={name => { setOpen(false); onCreate(name) }} />
      </Popover>
    </>
  )
}

function BoardMenu({ collection, onRename, onDelete }) {
  const [open, setOpen] = useState(false)
  const [view, setView] = useState('menu')   // menu | rename | delete
  const anchor = useRef(null)
  const close = () => { setOpen(false); setTimeout(() => setView('menu'), 150) }
  return (
    <>
      <button ref={anchor} className="ib-tab-more" aria-label={'Options for ' + collection.name} onClick={() => setOpen(o => !o)}>
        <I.more size={14} />
      </button>
      <Popover anchor={anchor} open={open} onClose={close} width={view === 'menu' ? 200 : 300}>
        {view === 'menu' && (
          <div className="ib-menu" role="menu">
            <MenuItem icon={I.pencil} onClick={() => setView('rename')}>Rename board</MenuItem>
            <MenuItem icon={I.trash} danger onClick={() => setView('delete')}>Delete board</MenuItem>
          </div>
        )}
        {view === 'rename' && (
          <NameForm title="Rename board" initial={collection.name} cta="Save" onCancel={close}
            onSave={name => { close(); if (name !== collection.name) onRename(name) }} />
        )}
        {view === 'delete' && (
          <div style={{ padding: 16 }}>
            <div style={S.popTitle}>Delete {collection.name}?</div>
            <div style={S.popBody}>The pictures stay on your inspiration board. They move to Unsorted.</div>
            <div style={S.popActions}>
              <Button variant="quiet" size="sm" onClick={close}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => { close(); onDelete() }}>Delete board</Button>
            </div>
          </div>
        )}
      </Popover>
    </>
  )
}

function NameForm({ title, initial = '', placeholder, cta, onSave, onCancel }) {
  const [v, setV] = useState(initial)
  return (
    <form style={{ padding: 16 }} onSubmit={e => { e.preventDefault(); if (v.trim()) onSave(v.trim()) }}>
      <div style={S.popTitle}>{title}</div>
      <Field autoFocus value={v} onChange={e => setV(e.target.value)} placeholder={placeholder} maxLength={80}
        onFocus={e => e.target.select()} />
      <div style={S.popActions}>
        <Button variant="quiet" size="sm" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" size="sm" type="submit" disabled={!v.trim()}>{cta}</Button>
      </div>
    </form>
  )
}

/* ── Empty / loading ───────────────────────────────────────────────────────── */

function SkeletonGrid() {
  const heights = [220, 300, 180, 260, 340, 200, 240, 280, 190, 320]
  return (
    <div className="ib-masonry" aria-hidden="true">
      {heights.map((h, i) => <div key={i} className="ib-skel" style={{ height: h, animationDelay: (i * 70) + 'ms' }} />)}
    </div>
  )
}

function EmptyState({ tab, hasAny, isTeam, boardName, onPick, onLink }) {
  if (hasAny) {
    const msg = tab === 'fav' ? 'Tap the heart on any picture to keep your favorites here.'
      : tab === 'unsorted' ? 'Everything has been sorted into a board.'
      : 'Nothing on ' + (boardName || 'this board') + ' yet. Add pictures while it is open, or drag pictures onto its tab from All.'
    return (
      <div style={S.emptyQuiet}>
        <div style={S.emptyIcon}>{tab === 'fav' ? <I.heart size={22} /> : <I.folder size={22} />}</div>
        {msg}
      </div>
    )
  }
  return (
    <div style={S.emptyHero}>
      <div style={S.emptyIconLg}><I.image size={30} sw={1.3} /></div>
      <div style={S.emptyTitle}>{isTeam ? 'No inspiration yet' : 'Start your inspiration board'}</div>
      <div style={S.emptyBody}>
        {isTeam
          ? 'Pictures the client adds from their Client Hub appear here. You can add pictures for them too.'
          : 'Collect pictures of homes, rooms, materials and details you love. Drop in photos and screenshots, or paste a link from Pinterest, Houzz or any website. Sort them into boards like Kitchen or Exterior, and tell us what caught your eye.'}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="primary" icon={I.upload} onClick={onPick}>Add pictures</Button>
        <Button variant="ghost" icon={I.link} onClick={onLink}>Add link</Button>
      </div>
      <div style={S.emptyHint}>You can also drag pictures here or paste a screenshot.</div>
    </div>
  )
}

/* ── Pin card ──────────────────────────────────────────────────────────────── */

// Card frame shapes (height / width). Inside these, the frame matches the picture exactly; beyond them
// (a very tall Pinterest graphic, a wide panorama) the frame stops and the picture fills it, trimmed
// evenly at the edges, the way Pinterest does. Unknown size (rare): a calm 4:5 until the picture loads.
const MIN_RATIO = 0.56, MAX_RATIO = 1.8

export function PinCard({ item, collections, showCollection, onOpen, onHeart, onImageError }) {
  const [loaded, setLoaded] = useState(false)
  const [nat, setNat] = useState(null)
  const w = item.width || (nat && nat.w), h = item.height || (nat && nat.h)
  const ratio = w && h ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, h / w)) : 1.25
  const col = showCollection && item.collectionId ? collections.find(c => c.id === item.collectionId) : null
  const nComments = item.comments.length
  return (
    <div className="ib-pin" role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      aria-label={item.note || item.title || 'Open picture'}>
      <div className="ib-pin-img" style={{ aspectRatio: '1 / ' + ratio }}>
        {item.imageUrl
          ? <img src={item.imageUrl} alt="" loading="lazy" draggable={false} className={loaded ? 'is-loaded' : ''}
              onLoad={e => { setLoaded(true); if (!item.width && e.currentTarget.naturalWidth) setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }) }}
              onError={() => onImageError && onImageError()}
              ref={el => { if (el && !loaded && el.complete && el.naturalWidth) setLoaded(true) }} />
          : <div style={{ ...S.missingImg, position: 'absolute', inset: 0, justifyContent: 'center' }}><I.image size={22} /><span>Picture unavailable</span></div>}
        <div className="ib-pin-shade" />
        <button className={'ib-heart' + (item.favorite ? ' on' : '')} aria-label={item.favorite ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={item.favorite} onClick={e => { e.stopPropagation(); onHeart() }}>
          <I.heart size={17} sw={1.8} fill={item.favorite ? 'currentColor' : 'none'} />
        </button>
        {nComments > 0 && <span className="ib-badge"><I.chat size={12} sw={1.8} />{nComments}</span>}
      </div>
      {(item.note || item.title || col || item.sourceUrl) && (
        <div style={S.pinMeta}>
          {item.note && <div style={S.pinNote}>{item.note}</div>}
          {!item.note && item.title && <div style={S.pinNote}>{item.title}</div>}
          {(col || item.sourceUrl) && (
            <div style={S.pinSub}>
              {col && <span>{col.name}</span>}
              {col && item.sourceUrl && <span style={{ opacity: 0.5 }}>·</span>}
              {item.sourceUrl && <span>{hostOf(item.sourceUrl)}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Add link ──────────────────────────────────────────────────────────────── */

// Forgiving: "pinterest.com/pin/123" or "www.houzz.com/photos" is a web address too. Only add the
// scheme when the text has no spaces and a dot in its first segment, so plain words still read invalid.
function normalizeUrl(s) {
  const t = String(s || '').trim()
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (/^[^\s/]+\.[^\s/]{2,}(\/\S*)?$/.test(t)) return 'https://' + t
  return t
}
function looksLikeUrl(s) { try { const u = new URL(normalizeUrl(s)); return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.') } catch { return false } }

// Every web address in a block of pasted text (one per line, or separated by spaces).
function extractUrls(text) {
  const out = []
  for (const part of String(text || '').split(/\s+/)) {
    const n = normalizeUrl(part.replace(/[)\],.;]+$/, ''))
    if (n && looksLikeUrl(n) && !out.includes(n)) out.push(n)
  }
  return out
}

function LinkDialog({ collections, covers, counts, initialBoard, onCreateBoard, onSave, onCancel, onFiles }) {
  const [url, setUrl] = useState('')
  const [note, setNote] = useState('')
  const [boardId, setBoardId] = useState(initialBoard || null)
  const [saving, setSaving] = useState(false)
  const [touched, setTouched] = useState(false)
  const valid = looksLikeUrl(url)
  return (
    <div className="ib-veil" onMouseDown={e => { if (e.target === e.currentTarget) onCancel() }}>
      <form className="ib-modal"
        onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
        onDrop={e => {
          // Dropped on the window itself: a picture file goes straight on the board; a picture or
          // link dragged from a website fills in the link.
          e.preventDefault(); e.stopPropagation()
          const dt = e.dataTransfer
          const files = Array.from(dt.files || []).filter(f => /^image\//.test(f.type) || !f.type)
          if (files.length) { onFiles(files); return }
          const html = dt.getData('text/html')
          const src = html && ((html.match(/<img[^>]+src=["']([^"']+)["']/i) || [])[1] || '').replace(/&amp;/g, '&')
          const link = (src && /^https?:/i.test(src) ? src : '') || (dt.getData('text/uri-list') || '').split(/\r?\n/)[0] || dt.getData('text/plain')
          if (link) { setUrl(link.trim()); setTouched(true) }
        }}
        onSubmit={async e => {
        e.preventDefault(); setTouched(true); if (!valid) return
        setSaving(true); const ok = await onSave(normalizeUrl(url), note.trim(), boardId); if (!ok) setSaving(false)
      }}>
        <div style={S.modalHead}>
          <div>
            <div style={S.eyebrow}>Add from the web</div>
            <div style={S.modalTitle}>Save a link</div>
          </div>
          <IconButton label="Close" onClick={onCancel}><I.close size={16} /></IconButton>
        </div>
        <div style={S.modalHelp}>Paste a link to a picture or a page from Pinterest, Houzz, a magazine or a maker&rsquo;s site. We&rsquo;ll save its picture to your board.</div>

        <label style={S.fieldLabel}>Link</label>
        <Field autoFocus value={url} onChange={e => setUrl(e.target.value)} onBlur={() => setTouched(true)}
          placeholder="https://www.pinterest.com/pin/…" leading={<I.link size={15} />}
          invalid={touched && url.trim() !== '' && !valid} inputMode="url" />
        {touched && url.trim() !== '' && !valid && <div style={S.fieldErr}>That doesn&rsquo;t look like a web address. Try copying the link from your browser&rsquo;s address bar.</div>}

        <label style={S.fieldLabel}>Save to</label>
        <BoardPicker value={boardId} onChange={setBoardId} collections={collections} covers={covers} counts={counts} onCreate={onCreateBoard} />

        <label style={S.fieldLabel}>What do you like about it? <span style={S.optional}>Optional</span></label>
        <Field as="textarea" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="The color of the cabinets, the open shelving…" />

        <div style={{ ...S.popActions, marginTop: 22 }}>
          <Button variant="quiet" type="button" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving || !url.trim()} loading={saving}>
            {saving ? 'Finding the picture' : 'Save to board'}
          </Button>
        </div>
      </form>
    </div>
  )
}

/* ── Pin detail ────────────────────────────────────────────────────────────── */

function PinDetail({ item, isTeam, collections, covers, counts, busy, onImageError, position, hasPrev, hasNext, onPrev, onNext, onClose, onCreateBoard, onUpdate, onDelete, onComment, onDeleteComment }) {
  const canEditNote = isTeam || item.addedByKind === 'client'
  const canRemove = isTeam || item.addedByKind === 'client'
  const [note, setNote] = useState(item.note || '')
  const [noteSaved, setNoteSaved] = useState(false)
  const [comment, setComment] = useState('')
  const [share, setShare] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)
  useEffect(() => { setNote(item.note || ''); setConfirmDel(false); setNoteSaved(false) }, [item.id, item.note])
  useEffect(() => { setImgLoaded(false) }, [item.id])

  useEffect(() => {
    function onKey(e) {
      const tag = (document.activeElement && document.activeElement.tagName) || ''
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowLeft' && hasPrev) onPrev()
      if (e.key === 'ArrowRight' && hasNext) onNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [hasPrev, hasNext, onPrev, onNext])

  async function saveNote() {
    if ((note.trim() || null) === (item.note || null)) return
    const b = await onUpdate({ note: note.trim() })
    if (b) { setNoteSaved(true); setTimeout(() => setNoteSaved(false), 1800) }
  }

  async function submitComment(e) {
    e && e.preventDefault()
    const body = comment.trim()
    if (!body || busy) return
    const b = await onComment(body, isTeam && !share)
    if (b) setComment('')
  }

  const who = item.addedByName || (item.addedByKind === 'team' ? 'Alder James' : 'Client')

  return (
    <div className="ib-veil ib-veil--dark" onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="ib-detail">
        <div className="ib-detail-img">
          {item.imageUrl
            ? <img src={item.imageUrl} alt="" onLoad={() => setImgLoaded(true)} onError={() => onImageError && onImageError()} className={imgLoaded ? 'is-loaded' : ''}
                ref={el => { if (el && !imgLoaded && el.complete && el.naturalWidth) setImgLoaded(true) }} />
            : <div style={{ ...S.missingImg, color: 'rgba(255,255,255,0.6)' }}><I.image size={26} /><span>Picture unavailable</span></div>}
          {hasPrev && <button className="ib-nav ib-nav--l" onClick={onPrev} aria-label="Previous picture"><I.left size={20} /></button>}
          {hasNext && <button className="ib-nav ib-nav--r" onClick={onNext} aria-label="Next picture"><I.right size={20} /></button>}
          {position && <span className="ib-counter">{position}</span>}
        </div>

        <aside className="ib-detail-side">
          <div style={S.sideTop}>
            <button className={'ib-fav' + (item.favorite ? ' on' : '')} aria-pressed={item.favorite}
              onClick={() => onUpdate({ favorite: !item.favorite }, item.favorite ? 'Removed from favorites' : 'Added to favorites')}>
              <I.heart size={15} sw={1.8} fill={item.favorite ? 'currentColor' : 'none'} />
              {item.favorite ? 'Favorite' : 'Add to favorites'}
            </button>
            <div style={{ flex: 1 }} />
            <IconButton label="Close" onClick={onClose}><I.close size={17} /></IconButton>
          </div>

          <div style={S.byline}>
            <span className="ib-avatar" data-kind={item.addedByKind}>{initials(who)}</span>
            <div style={{ minWidth: 0 }}>
              <div style={S.bylineName}>{who}</div>
              <div style={S.bylineSub}>
                Added {fmtDate(item.createdAt)}
                {item.sourceUrl && <> · <a className="ib-src" href={item.sourceUrl} target="_blank" rel="noopener noreferrer">{hostOf(item.sourceUrl) || 'Source'}<I.external size={11} sw={1.8} /></a></>}
              </div>
            </div>
          </div>

          <label style={S.fieldLabel}>Board</label>
          <BoardPicker value={item.collectionId} collections={collections} covers={covers} counts={counts} disabled={busy}
            onCreate={onCreateBoard}
            onChange={id => onUpdate({ collectionId: id }, 'Moved to ' + (id ? (collections.find(c => c.id === id)?.name || 'board') : 'Unsorted'))} />

          <label style={S.fieldLabel}>
            {item.addedByKind === 'client' ? 'What caught your eye' : 'Note'}
            {noteSaved && <span style={S.savedChip}><I.check size={11} sw={2.2} />Saved</span>}
          </label>
          {canEditNote ? (
            <Field as="textarea" rows={3} value={note} onChange={e => setNote(e.target.value)} onBlur={saveNote}
              placeholder={isTeam ? 'Add a note' : 'Tell us what you love (or don’t) about this one'} />
          ) : (
            <div style={S.readNote}>{item.note || <span style={{ color: 'var(--ib-muted)' }}>No note</span>}</div>
          )}

          <div style={S.commentsHead}>
            <span>Conversation</span>
            {item.comments.length > 0 && <span style={S.commentCount}>{item.comments.length}</span>}
          </div>
          <div style={S.comments}>
            {item.comments.length === 0 && <div style={S.noComments}>{isTeam ? 'No notes or comments yet.' : 'Ask a question or tell us more. Our team will see it.'}</div>}
            {item.comments.map(c => (
              <div key={c.id} className={'ib-comment' + (c.internal ? ' is-internal' : '')}>
                <span className="ib-avatar ib-avatar--sm" data-kind={c.authorKind}>{initials(c.authorName || (c.authorKind === 'team' ? 'Alder James' : 'Client'))}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.commentHead}>
                    <span style={{ fontWeight: 600, color: 'var(--ib-text)' }}>{c.authorName || (c.authorKind === 'team' ? 'Alder James' : 'Client')}</span>
                    {c.internal && <span style={S.internalChip}><I.lock size={10} sw={2} />Team only</span>}
                    <span style={{ color: 'var(--ib-muted)' }}>{fmtDate(c.createdAt)}</span>
                    {(c.mine || isTeam) && (
                      <button className="ib-comment-del" onClick={() => onDeleteComment(c.id)} aria-label="Remove comment"><I.trash size={13} /></button>
                    )}
                  </div>
                  <div style={S.commentBody}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submitComment} className={'ib-composer' + (isTeam && !share ? ' is-internal' : '')}>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2}
              placeholder={isTeam ? (share ? 'Reply to the client' : 'Note for the team') : 'Write a comment'}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitComment(e) }} />
            <div style={S.composerBar}>
              {isTeam ? (
                <Switch checked={share} onChange={setShare} label={share ? 'Visible to client' : 'Team only'} />
              ) : <span style={S.composerHint}>Ctrl + Enter to send</span>}
              <button type="submit" className="ib-send" disabled={busy || !comment.trim()} aria-label="Send">
                <I.send size={15} sw={1.8} />
              </button>
            </div>
          </form>

          {canRemove && (
            <div style={{ marginTop: 'auto', paddingTop: 22 }}>
              {confirmDel ? (
                <div className="ib-confirm">
                  <span>Remove this picture from the board?</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="quiet" size="sm" onClick={() => setConfirmDel(false)}>Keep it</Button>
                    <Button variant="danger" size="sm" onClick={onDelete}>Remove</Button>
                  </div>
                </div>
              ) : (
                <button className="ib-remove" onClick={() => setConfirmDel(true)}><I.trash size={14} />Remove from board</button>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

/* ── Primitives ────────────────────────────────────────────────────────────── */

function Button({ variant = 'ghost', size = 'md', icon: Ic, loading, children, className = '', type = 'button', ...rest }) {
  return (
    <button type={type} className={`ib-btn ib-btn--${variant} ib-btn--${size} ${className}`} {...rest}>
      {loading ? <span className="ib-spin" /> : Ic ? <Ic size={size === 'sm' ? 13 : 15} sw={1.8} /> : null}
      <span>{children}</span>
    </button>
  )
}

function IconButton({ label, children, size = 34, ...rest }) {
  return <button type="button" className="ib-iconbtn" aria-label={label} title={label} style={{ width: size, height: size }} {...rest}>{children}</button>
}

function Field({ as = 'input', leading, invalid, ...rest }) {
  const El = as
  return (
    <div className={'ib-field' + (invalid ? ' is-invalid' : '') + (leading ? ' has-leading' : '') + (as === 'textarea' ? ' is-area' : '')}>
      {leading && <span className="ib-field-lead">{leading}</span>}
      <El {...rest} />
    </div>
  )
}

function Switch({ checked, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className={'ib-switch' + (checked ? ' on' : '')} onClick={() => onChange(!checked)}>
      <span className="ib-switch-track"><span className="ib-switch-knob" /></span>
      <span>{label}</span>
    </button>
  )
}

function MenuItem({ icon: Ic, danger, children, onClick }) {
  return (
    <button role="menuitem" className={'ib-menuitem' + (danger ? ' is-danger' : '')} onClick={onClick}>
      {Ic && <Ic size={15} />}<span>{children}</span>
    </button>
  )
}

/**
 * Popover — a floating panel anchored to an element, portaled to <body> so no overflow clips it.
 * Opens below the anchor (above when there is no room), closes on outside click, Escape (caught in
 * the capture phase so the board behind it stays open), resize and scroll outside the panel.
 */
function Popover({ anchor, open, onClose, width = 280, align = 'start', children }) {
  const panel = useRef(null)
  const [pos, setPos] = useState(null)
  const [render, setRender] = useState(open)
  useEffect(() => { if (open) setRender(true); else { const t = setTimeout(() => setRender(false), 140); return () => clearTimeout(t) } }, [open])

  useLayoutEffect(() => {
    if (!open || !anchor.current) return
    const place = () => {
      const r = anchor.current.getBoundingClientRect()
      const ph = panel.current ? panel.current.offsetHeight : 240
      const vw = window.innerWidth, vh = window.innerHeight
      const w = Math.min(width, vw - 16)
      let left = align === 'end' ? r.right - w : r.left
      left = Math.max(8, Math.min(left, vw - w - 8))
      const below = r.bottom + 8
      const up = below + ph > vh - 8 && r.top - 8 - ph > 8
      const next = { left: Math.round(left), top: Math.round(up ? r.top - 8 - ph : below), width: Math.round(w), up }
      // Only commit a real change — this runs after every render, so an unconditional set would loop.
      setPos(p => (p && p.left === next.left && p.top === next.top && p.width === next.width && p.up === next.up) ? p : next)
    }
    place()
    const id = requestAnimationFrame(place)
    return () => cancelAnimationFrame(id)
  }, [open, anchor, width, align, children])

  useEffect(() => {
    if (!open) return
    const onDown = e => {
      if (panel.current && panel.current.contains(e.target)) return
      if (anchor.current && anchor.current.contains(e.target)) return
      onClose()
    }
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    const onScroll = e => { if (panel.current && panel.current.contains(e.target)) return; onClose() }
    document.addEventListener('mousedown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onClose)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onClose)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, onClose, anchor])

  if (!render) return null
  const vars = anchor.current ? anchor.current.closest('.ib-root') : null
  const style = vars ? Object.fromEntries(Array.from(vars.style).filter(k => k.startsWith('--ib-')).map(k => [k, vars.style.getPropertyValue(k)])) : {}
  return createPortal(
    <div ref={panel} className={'ib-pop' + (open && pos ? ' is-open' : '') + (pos && pos.up ? ' is-up' : '')}
      style={{ ...style, left: pos ? pos.left : -9999, top: pos ? pos.top : -9999, width: pos ? pos.width : width }}>
      {children}
    </div>,
    document.body,
  )
}

/**
 * BoardPicker — searchable combobox for choosing which board a picture lives on. Type to filter;
 * arrow keys move, Enter picks; when nothing matches exactly, the last row offers to create a board
 * with that name and puts the picture on it.
 */
function BoardPicker({ value, onChange, collections, covers = {}, counts = {}, onCreate, disabled }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hi, setHi] = useState(0)
  const [creating, setCreating] = useState(false)
  const anchor = useRef(null)
  const listRef = useRef(null)
  const current = collections.find(c => c.id === value) || null

  const query = q.trim().toLowerCase()
  const options = useMemo(() => {
    const rows = [{ id: null, name: 'Unsorted', kind: 'unsorted' }, ...collections.map(c => ({ id: c.id, name: c.name, kind: 'board' }))]
    const filtered = query ? rows.filter(r => r.name.toLowerCase().includes(query)) : rows
    const exact = collections.some(c => c.name.trim().toLowerCase() === query)
    if (query && !exact && onCreate) filtered.push({ id: '__create__', name: q.trim(), kind: 'create' })
    return filtered
  }, [collections, query, q, onCreate])

  useEffect(() => { setHi(0) }, [query, open])
  useEffect(() => { if (!open) { setQ(''); setCreating(false) } }, [open])
  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector('[data-hi="1"]')
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [hi])

  async function choose(opt) {
    if (!opt) return
    if (opt.kind === 'create') {
      setCreating(true)
      const id = await onCreate(opt.name)
      setCreating(false)
      if (id) { onChange(id); setOpen(false) }
      return
    }
    setOpen(false)
    if (opt.id !== (value || null)) onChange(opt.id)
  }

  function onKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(options.length - 1, h + 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)) }
    else if (e.key === 'Enter') { e.preventDefault(); choose(options[hi]) }
  }

  return (
    <>
      <button ref={anchor} type="button" className={'ib-select' + (open ? ' is-open' : '')} disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true) } }}>
        <BoardThumb cover={current ? covers[current.id] : null} kind={current ? 'board' : 'unsorted'} />
        <span className="ib-select-label">{current ? current.name : 'Unsorted'}</span>
        <span className="ib-select-caret"><I.down size={15} /></span>
      </button>
      <Popover anchor={anchor} open={open} onClose={() => setOpen(false)} width={Math.max(260, anchor.current ? anchor.current.offsetWidth : 260)}>
        <div className="ib-combo">
          <div className="ib-combo-search">
            <I.search size={15} />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
              placeholder={onCreate ? 'Find or create a board' : 'Find a board'} aria-label="Search boards" />
          </div>
          <div className="ib-combo-list" role="listbox" ref={listRef}>
            {options.length === 0 && <div style={S.comboEmpty}>No boards match</div>}
            {options.map((o, i) => {
              const selected = o.kind !== 'create' && (o.id || null) === (value || null)
              return (
                <button key={o.id || '__unsorted__'} type="button" role="option" aria-selected={selected}
                  data-hi={i === hi ? '1' : undefined}
                  className={'ib-option' + (i === hi ? ' is-hi' : '') + (o.kind === 'create' ? ' is-create' : '')}
                  onMouseEnter={() => setHi(i)} onClick={() => choose(o)}>
                  {o.kind === 'create'
                    ? <span className="ib-thumb ib-thumb--create">{creating ? <span className="ib-spin ib-spin--dark" /> : <I.plus size={14} sw={2} />}</span>
                    : <BoardThumb cover={o.id ? covers[o.id] : null} kind={o.kind} />}
                  <span className="ib-option-label">
                    {o.kind === 'create' ? <>Create <strong>&ldquo;{o.name}&rdquo;</strong></> : o.name}
                  </span>
                  {o.kind !== 'create' && <span className="ib-option-count">{o.id ? (counts[o.id] || 0) : (counts.unsorted || 0)}</span>}
                  <span className="ib-option-check">{selected && <I.check size={15} sw={2} />}</span>
                </button>
              )
            })}
          </div>
        </div>
      </Popover>
    </>
  )
}

function BoardThumb({ cover, kind }) {
  if (cover) return <span className="ib-thumb" style={{ backgroundImage: `url("${cover}")` }} />
  return <span className="ib-thumb ib-thumb--empty">{kind === 'unsorted' ? <I.inbox size={13} /> : <I.folder size={13} />}</span>
}

/* ── Styles ────────────────────────────────────────────────────────────────── */

const FONT = '"DM Sans", system-ui, sans-serif'
const DISPLAY = '"Montserrat", sans-serif'
const MONO = '"DM Mono", ui-monospace, monospace'
const EASE = 'cubic-bezier(.2,.7,.2,1)'

export const CSS = `
.ib-root, .ib-pop { -webkit-font-smoothing: antialiased; }
.ib-root *, .ib-pop * { box-sizing: border-box; }
.ib-root ::selection { background: rgba(176,141,87,0.28); }
.ib-root button, .ib-pop button { font-family: ${FONT}; }
.ib-root :focus-visible, .ib-pop :focus-visible { outline: none; box-shadow: 0 0 0 3px var(--ib-ring); }
.ib-root input, .ib-root textarea, .ib-pop input, .ib-pop textarea { box-shadow: none !important; outline: none !important; border-radius: 0; -webkit-appearance: none; appearance: none; }

/* Buttons */
.ib-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: 999px; cursor: pointer; white-space: nowrap;
  font-weight: 500; letter-spacing: .01em; border: 1px solid transparent; transition: background .18s ${EASE}, color .18s, border-color .18s, transform .12s ${EASE}, box-shadow .18s, opacity .18s; }
.ib-btn:active:not(:disabled) { transform: scale(.97); }
.ib-btn:disabled { opacity: .45; cursor: default; }
.ib-btn--md { height: 40px; padding: 0 20px; font-size: 13.5px; }
.ib-btn--sm { height: 32px; padding: 0 14px; font-size: 12.5px; }
.ib-btn--primary { background: var(--ib-accent); color: var(--ib-on-accent); box-shadow: 0 1px 2px rgba(7,39,45,.12), 0 4px 14px rgba(7,39,45,.10); }
.ib-btn--primary:hover:not(:disabled) { box-shadow: 0 2px 4px rgba(7,39,45,.16), 0 10px 24px rgba(7,39,45,.16); transform: translateY(-1px); }
.ib-btn--ghost { background: var(--ib-surface); color: var(--ib-text); border-color: var(--ib-border); }
.ib-btn--ghost:hover:not(:disabled) { border-color: var(--ib-border-strong); background: var(--ib-surface); box-shadow: 0 4px 14px rgba(7,39,45,.07); }
.ib-btn--quiet { background: transparent; color: var(--ib-mid); }
.ib-btn--quiet:hover:not(:disabled) { background: var(--ib-hover); color: var(--ib-text); }
.ib-btn--danger { background: ${HEART}; color: #fff; }
.ib-btn--danger:hover:not(:disabled) { background: #a9343f; }

.ib-iconbtn { display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; border: none; background: transparent; color: var(--ib-mid);
  cursor: pointer; transition: background .16s, color .16s, transform .12s; flex-shrink: 0; }
.ib-iconbtn:hover { background: var(--ib-hover); color: var(--ib-text); }
.ib-iconbtn:active { transform: scale(.94); }

.ib-spin { width: 14px; height: 14px; border-radius: 50%; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; animation: ib-spin .7s linear infinite; }
.ib-spin--dark { border-color: var(--ib-border); border-top-color: var(--ib-text); }
@keyframes ib-spin { to { transform: rotate(360deg); } }

/* Fields */
.ib-field { position: relative; display: flex; align-items: center; background: var(--ib-surface); border: 1px solid var(--ib-border); border-radius: 10px;
  transition: border-color .16s, box-shadow .16s, background .16s; }
.ib-field:hover { border-color: var(--ib-border-strong); }
.ib-field:focus-within { border-color: var(--ib-accent); box-shadow: 0 0 0 4px var(--ib-ring); }
.ib-field.is-invalid { border-color: ${HEART}; box-shadow: 0 0 0 4px rgba(194,65,79,.12); }
.ib-field input, .ib-field textarea { flex: 1; width: 100%; min-width: 0; border: none; outline: none; background: transparent; color: var(--ib-text);
  font: 400 14px/1.5 ${FONT}; padding: 11px 14px; resize: none; }
.ib-field.is-area textarea { min-height: 78px; }
.ib-field input::placeholder, .ib-field textarea::placeholder { color: var(--ib-muted); opacity: 1; }
.ib-field.has-leading input { padding-left: 4px; }
.ib-field-lead { display: inline-flex; padding-left: 13px; color: var(--ib-muted); }
.ib-field:focus-within .ib-field-lead { color: var(--ib-text); }

/* Select trigger */
.ib-select { width: 100%; height: 46px; display: flex; align-items: center; gap: 11px; padding: 0 12px 0 7px; cursor: pointer; text-align: left;
  background: var(--ib-surface); border: 1px solid var(--ib-border); border-radius: 10px; color: var(--ib-text); font-size: 14px;
  transition: border-color .16s, box-shadow .16s; }
.ib-select:hover:not(:disabled) { border-color: var(--ib-border-strong); }
.ib-select.is-open { border-color: var(--ib-accent); box-shadow: 0 0 0 4px var(--ib-ring); }
.ib-select:disabled { opacity: .6; cursor: default; }
.ib-select-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
.ib-select-caret { color: var(--ib-muted); display: inline-flex; transition: transform .2s ${EASE}; }
.ib-select.is-open .ib-select-caret { transform: rotate(180deg); color: var(--ib-text); }
.ib-thumb { width: 32px; height: 32px; border-radius: 7px; background: var(--ib-subtle) center/cover no-repeat; flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(7,39,45,.06); display: inline-flex; align-items: center; justify-content: center; color: var(--ib-muted); }
.ib-thumb--create { background: transparent; border: 1px dashed var(--ib-border-strong); color: var(--ib-text); box-shadow: none; }

/* Popover */
.ib-pop { position: fixed; z-index: 1200; background: var(--ib-surface, #fff); color: var(--ib-text, #07272D); border-radius: 14px; font-family: ${FONT};
  border: 1px solid var(--ib-border, rgba(7,39,45,.1)); box-shadow: 0 2px 6px rgba(7,39,45,.06), 0 18px 48px rgba(7,39,45,.18);
  opacity: 0; transform: translateY(-4px) scale(.98); transform-origin: top left; transition: opacity .14s ${EASE}, transform .16s ${EASE}; pointer-events: none; }
.ib-pop.is-up { transform-origin: bottom left; transform: translateY(4px) scale(.98); }
.ib-pop.is-open { opacity: 1; transform: none; pointer-events: auto; }
.ib-combo-search { display: flex; align-items: center; gap: 9px; padding: 0 14px; height: 46px; border-bottom: 1px solid var(--ib-border); color: var(--ib-muted); }
.ib-combo-search input { flex: 1; min-width: 0; padding: 0; border: none; outline: none; background: transparent; color: var(--ib-text); font: 400 14px ${FONT}; height: 100%; }
.ib-combo-search input::placeholder { color: var(--ib-muted); }
.ib-combo-list { max-height: 292px; overflow-y: auto; padding: 6px; }
.ib-option { width: 100%; display: flex; align-items: center; gap: 11px; padding: 6px 8px; border: none; background: transparent; border-radius: 9px;
  color: var(--ib-text); cursor: pointer; text-align: left; font-size: 14px; transition: background .1s; }
.ib-option.is-hi { background: var(--ib-hover); }
.ib-option-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ib-option-label strong { font-weight: 600; }
.ib-option-count { font: 400 11.5px ${MONO}; color: var(--ib-muted); }
.ib-option-check { width: 16px; display: inline-flex; color: var(--ib-text); }
.ib-option.is-create { margin-top: 4px; border-top: 1px solid var(--ib-border); border-radius: 0 0 9px 9px; padding-top: 10px; }
.ib-menu { padding: 6px; }
.ib-menuitem { width: 100%; display: flex; align-items: center; gap: 10px; padding: 9px 10px; border: none; background: transparent; border-radius: 9px;
  color: var(--ib-text); font-size: 13.5px; cursor: pointer; text-align: left; transition: background .1s; }
.ib-menuitem:hover, .ib-menuitem:focus-visible { background: var(--ib-hover); }
.ib-menuitem.is-danger { color: ${HEART}; }

/* Switch */
.ib-switch { display: inline-flex; align-items: center; gap: 9px; border: none; background: transparent; cursor: pointer; padding: 2px; border-radius: 999px;
  font-size: 12.5px; color: var(--ib-mid); }
.ib-switch-track { width: 32px; height: 18px; border-radius: 999px; background: var(--ib-border-strong); position: relative; transition: background .2s ${EASE}; }
.ib-switch-knob { position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); transition: transform .22s ${EASE}; }
.ib-switch.on .ib-switch-track { background: #2f7d6b; }
.ib-switch.on .ib-switch-knob { transform: translateX(14px); }
.ib-switch.on { color: var(--ib-text); }

/* Tabs */
.ib-tabs::-webkit-scrollbar { height: 0; }
.ib-tab { display: inline-flex; align-items: center; height: 36px; border-radius: 999px; background: var(--ib-surface); border: 1px solid var(--ib-border);
  color: var(--ib-text); flex-shrink: 0; transition: background .18s ${EASE}, color .18s, border-color .18s, transform .15s, box-shadow .18s; }
.ib-tab:hover { border-color: var(--ib-border-strong); }
.ib-tab-main { display: inline-flex; align-items: center; gap: 7px; height: 100%; padding: 0 15px; border: none; background: transparent; color: inherit;
  cursor: pointer; font-size: 13.5px; font-weight: 500; border-radius: 999px; white-space: nowrap; }
.ib-tab-label { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ib-tab-count { font: 400 11.5px ${MONO}; color: var(--ib-muted); transition: color .18s; }
.ib-tab.is-active { background: var(--ib-accent); color: var(--ib-on-accent); border-color: transparent; box-shadow: 0 4px 14px rgba(7,39,45,.14); }
.ib-tab.is-active .ib-tab-count { color: var(--ib-on-accent); opacity: .7; }
.ib-tab.is-drop { transform: scale(1.06); box-shadow: 0 0 0 3px rgba(176,141,87,.55); }
.ib-tab-more { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; margin: 0 5px 0 -8px; border-radius: 50%;
  border: none; background: transparent; color: inherit; opacity: .75; cursor: pointer; transition: background .15s, opacity .15s; }
.ib-tab-more:hover { opacity: 1; background: rgba(255,255,255,.16); }
.ib-tab--new { gap: 7px; padding: 0 15px; cursor: pointer; font-size: 13.5px; font-weight: 500; color: var(--ib-mid); border-style: dashed; background: transparent; }
.ib-tab--new:hover { color: var(--ib-text); background: var(--ib-surface); }

/* Masonry + pins */
.ib-masonry { column-width: 240px; column-gap: 18px; }
.ib-pin { break-inside: avoid; margin: 0; background: var(--ib-surface); border-radius: 14px; overflow: hidden; cursor: zoom-in;
  box-shadow: 0 1px 2px rgba(7,39,45,.05); transition: box-shadow .25s ${EASE}, transform .25s ${EASE}; animation: ib-rise .45s ${EASE} both; }
.ib-pin:hover { box-shadow: 0 14px 34px rgba(7,39,45,.14); transform: translateY(-2px); }
@keyframes ib-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.ib-pin-img { position: relative; background: var(--ib-subtle); overflow: hidden; }
.ib-pin-img img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; object-fit: cover; object-position: center; opacity: 0; transform: scale(1.015); transition: opacity .45s ${EASE}, transform .6s ${EASE}; }
.ib-pin-img img.is-loaded { opacity: 1; transform: none; }
.ib-pin:hover .ib-pin-img img.is-loaded { transform: scale(1.025); }
/* While a picture is being carried, the cards it passes over stay still. */
body.ib-dragging .ib-pin:hover { transform: none; box-shadow: 0 1px 2px rgba(7,39,45,.05); }
body.ib-dragging .ib-pin:hover .ib-pin-img img.is-loaded { transform: none; }
body.ib-dragging .ib-pin:hover .ib-heart:not(.on), body.ib-dragging .ib-pin:hover .ib-pin-shade { opacity: 0; }
.ib-pin-shade { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(7,39,45,.22) 0%, rgba(7,39,45,0) 34%); opacity: 0; transition: opacity .25s; pointer-events: none; }
.ib-pin:hover .ib-pin-shade { opacity: 1; }
.ib-heart { position: absolute; top: 10px; right: 10px; width: 36px; height: 36px; border-radius: 50%; border: none; cursor: pointer; display: flex;
  align-items: center; justify-content: center; background: rgba(255,255,255,.94); color: #07272D; backdrop-filter: blur(6px);
  opacity: 0; transform: scale(.9); transition: opacity .2s, transform .2s ${EASE}, color .2s; box-shadow: 0 2px 8px rgba(0,0,0,.14); }
.ib-heart:hover { transform: scale(1.06); }
.ib-heart.on { opacity: 1; transform: none; color: ${HEART}; }
.ib-pin:hover .ib-heart, .ib-pin:focus-within .ib-heart { opacity: 1; transform: none; }
@media (hover: none) { .ib-heart { opacity: 1; transform: none; } }
.ib-badge { position: absolute; left: 10px; bottom: 10px; display: inline-flex; align-items: center; gap: 5px; background: rgba(7,39,45,.72);
  backdrop-filter: blur(6px); color: #fff; font: 500 11px ${MONO}; padding: 4px 9px; border-radius: 999px; }
.ib-skel { break-inside: avoid; margin: 0 0 18px; border-radius: 14px; background: linear-gradient(100deg, var(--ib-subtle) 30%, var(--ib-surface) 50%, var(--ib-subtle) 70%);
  background-size: 300% 100%; animation: ib-shimmer 1.6s ease-in-out infinite; }
@keyframes ib-shimmer { from { background-position: 100% 0; } to { background-position: 0 0; } }

/* Overlays */
.ib-veil { position: fixed; inset: 0; z-index: 1000; background: rgba(7,39,45,.32); backdrop-filter: blur(3px); display: flex; align-items: center;
  justify-content: center; padding: 16px; animation: ib-fade .18s ease both; }
.ib-veil--dark { background: rgba(5,18,21,.78); padding: 3vh 3vw; }
@keyframes ib-fade { from { opacity: 0; } to { opacity: 1; } }
.ib-modal { width: 480px; max-width: 100%; max-height: calc(100vh - 32px); overflow-y: auto; background: var(--ib-surface); border-radius: 18px; padding: 24px 26px 22px;
  box-shadow: 0 30px 80px rgba(7,39,45,.28); animation: ib-pop .26s ${EASE} both; }
@keyframes ib-pop { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
.ib-detail { display: flex; width: 100%; max-width: 1240px; height: 100%; max-height: 860px; background: var(--ib-surface); border-radius: 18px; overflow: hidden;
  box-shadow: 0 40px 100px rgba(0,0,0,.4); animation: ib-pop .3s ${EASE} both; }
.ib-detail-img { flex: 1; position: relative; min-width: 0; background: #0b1b1e; display: flex; align-items: center; justify-content: center; }
.ib-detail-img img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; opacity: 0; transition: opacity .4s ${EASE}; }
.ib-detail-img img.is-loaded { opacity: 1; }
.ib-nav { position: absolute; top: 50%; width: 44px; height: 44px; margin-top: -22px; border-radius: 50%; border: none; cursor: pointer; display: flex;
  align-items: center; justify-content: center; background: rgba(255,255,255,.14); color: #fff; backdrop-filter: blur(8px); transition: background .18s, transform .18s; }
.ib-nav:hover { background: rgba(255,255,255,.28); transform: scale(1.05); }
.ib-nav--l { left: 16px; } .ib-nav--r { right: 16px; }
.ib-counter { position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); font: 400 11px ${MONO}; letter-spacing: .08em;
  color: rgba(255,255,255,.75); background: rgba(0,0,0,.3); padding: 4px 10px; border-radius: 999px; backdrop-filter: blur(6px); }
.ib-detail-side { width: 380px; flex-shrink: 0; padding: 18px 24px 22px; display: flex; flex-direction: column; overflow-y: auto; }
.ib-fav { display: inline-flex; align-items: center; gap: 8px; height: 36px; padding: 0 16px; border-radius: 999px; border: 1px solid var(--ib-border);
  background: var(--ib-surface); color: var(--ib-text); font-size: 13px; font-weight: 500; cursor: pointer; transition: all .18s ${EASE}; }
.ib-fav:hover { border-color: var(--ib-border-strong); }
.ib-fav.on { background: rgba(194,65,79,.08); border-color: rgba(194,65,79,.3); color: ${HEART}; }
.ib-avatar { width: 34px; height: 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  font: 600 12px ${FONT}; letter-spacing: .03em; background: var(--ib-subtle); color: var(--ib-text); }
.ib-avatar[data-kind="team"] { background: #07272D; color: #e9dcc4; }
.ib-avatar--sm { width: 28px; height: 28px; font-size: 10.5px; }
.ib-src { color: var(--ib-mid); text-decoration: none; display: inline-flex; align-items: center; gap: 3px; border-bottom: 1px solid var(--ib-border); }
.ib-src:hover { color: var(--ib-text); border-color: var(--ib-text); }
.ib-comment { display: flex; gap: 10px; padding: 10px; border-radius: 12px; transition: background .15s; }
.ib-comment:hover { background: var(--ib-hover); }
.ib-comment.is-internal { background: rgba(176,141,87,.10); }
.ib-comment-del { margin-left: auto; border: none; background: transparent; color: var(--ib-muted); cursor: pointer; padding: 3px; border-radius: 6px;
  opacity: 0; display: inline-flex; transition: opacity .15s, color .15s, background .15s; }
.ib-comment:hover .ib-comment-del, .ib-comment-del:focus-visible { opacity: 1; }
.ib-comment-del:hover { color: ${HEART}; background: rgba(194,65,79,.08); }
.ib-composer { border: 1px solid var(--ib-border); border-radius: 14px; background: var(--ib-surface); transition: border-color .16s, box-shadow .16s; }
.ib-composer:focus-within { border-color: var(--ib-accent); box-shadow: 0 0 0 4px var(--ib-ring); }
.ib-composer.is-internal { background: rgba(176,141,87,.06); }
.ib-composer textarea { display: block; width: 100%; border: none; outline: none; background: transparent; resize: none; color: var(--ib-text);
  font: 400 14px/1.5 ${FONT}; padding: 12px 14px 4px; }
.ib-composer textarea::placeholder { color: var(--ib-muted); }
.ib-send { width: 34px; height: 34px; border-radius: 50%; border: none; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
  background: var(--ib-accent); color: var(--ib-on-accent); transition: transform .15s ${EASE}, opacity .15s; }
.ib-send:hover:not(:disabled) { transform: scale(1.06); }
.ib-send:disabled { opacity: .25; cursor: default; }
.ib-remove { display: inline-flex; align-items: center; gap: 7px; border: none; background: transparent; color: var(--ib-muted); font-size: 12.5px;
  cursor: pointer; padding: 6px 8px; margin-left: -8px; border-radius: 8px; transition: color .15s, background .15s; }
.ib-remove:hover { color: ${HEART}; background: rgba(194,65,79,.07); }
.ib-confirm { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 14px; border-radius: 12px;
  background: rgba(194,65,79,.07); font-size: 13px; color: var(--ib-text); animation: ib-fade .18s ease both; }

/* Drop + feedback */
.ib-dropveil { position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; pointer-events: none;
  background: rgba(246,244,240,.72); backdrop-filter: blur(4px); animation: ib-fade .15s ease both; }
.ib-dropcard { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 34px 48px; border-radius: 20px; border: 2px dashed var(--ib-gold);
  background: rgba(255,255,255,.9); color: #07272D; box-shadow: 0 20px 60px rgba(7,39,45,.16); }
.ib-progress { position: absolute; top: 0; left: 0; right: 0; height: 3px; z-index: 30; background: rgba(176,141,87,.18); }
.ib-progress > div { height: 100%; background: var(--ib-gold); transition: width .3s ${EASE}; }
.ib-toast { position: fixed; left: 50%; bottom: 28px; transform: translateX(-50%); z-index: 1300; background: #07272D; color: #fff; font: 500 13px ${FONT};
  padding: 11px 20px; border-radius: 999px; box-shadow: 0 12px 34px rgba(7,39,45,.3); animation: ib-toast .28s ${EASE} both; max-width: calc(100vw - 32px); text-align: center; }
.ib-toast--err { background: #8f2e38; }
.ib-hint { flex-basis: 100%; text-align: right; font-size: 12px; color: var(--ib-muted); margin-top: -6px; }
@media (hover: none), (max-width: 760px) { .ib-hint { display: none; } }
@keyframes ib-toast { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }

@media (max-width: 760px) {
  .ib-masonry { column-width: 150px; column-gap: 10px; }
  .ib-skel { margin-bottom: 10px; border-radius: 12px; }
  .ib-btn--md { height: 38px; padding: 0 15px; }
  .ib-veil--dark { padding: 0; }
  .ib-detail { flex-direction: column; max-height: none; border-radius: 0; overflow-y: auto; }
  .ib-detail-img { flex: none; height: 54vh; }
  .ib-detail-side { width: auto; overflow: visible; }
  .ib-comment-del { opacity: 1; }
}
`

const S = {
  root: { position: 'fixed', inset: 0, zIndex: 900, background: 'var(--ib-bg)', color: 'var(--ib-text)', fontFamily: FONT, display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, padding: '26px 32px 14px', flexWrap: 'wrap' },
  eyebrow: { fontFamily: DISPLAY, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--ib-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  title: { fontFamily: DISPLAY, fontSize: 26, fontWeight: 300, letterSpacing: '0.14em', textTransform: 'uppercase', margin: '6px 0 0', color: 'var(--ib-text)' },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10 },
  tabsRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 32px 16px', overflowX: 'auto', flexShrink: 0 },
  tabDivider: { width: 1, height: 20, background: 'var(--ib-border)', margin: '0 4px', flexShrink: 0 },
  boardNote: { padding: '0 32px 10px', fontSize: 12, color: 'var(--ib-muted)' },
  scroll: { flex: 1, overflowY: 'auto', padding: '4px 32px 60px' },
  emptyQuiet: { maxWidth: 360, margin: '12vh auto 0', textAlign: 'center', color: 'var(--ib-mid)', fontSize: 14, lineHeight: 1.6 },
  emptyIcon: { display: 'flex', justifyContent: 'center', color: 'var(--ib-muted)', marginBottom: 12 },
  emptyHero: { maxWidth: 540, margin: '9vh auto 0', textAlign: 'center', padding: '44px 36px 36px', background: 'var(--ib-surface)', borderRadius: 20, boxShadow: '0 1px 2px rgba(7,39,45,.04), 0 20px 60px rgba(7,39,45,.07)' },
  emptyIconLg: { width: 64, height: 64, margin: '0 auto 18px', borderRadius: '50%', background: 'var(--ib-subtle)', color: 'var(--ib-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyTitle: { fontFamily: DISPLAY, fontSize: 16, fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 },
  emptyBody: { fontSize: 14.5, lineHeight: 1.65, color: 'var(--ib-mid)', marginBottom: 26 },
  emptyHint: { fontSize: 12, color: 'var(--ib-muted)', marginTop: 18 },
  pinMeta: { padding: '11px 14px 13px' },
  pinNote: { fontSize: 13.5, lineHeight: 1.45, color: 'var(--ib-text)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  pinSub: { display: 'flex', gap: 6, fontSize: 11.5, color: 'var(--ib-muted)', marginTop: 5 },
  missingImg: { padding: '44px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ib-muted)' },
  dropTitle: { fontFamily: DISPLAY, fontSize: 14, letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 4 },
  dropSub: { fontSize: 13, color: '#4a6166' },
  modalHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 },
  modalTitle: { fontFamily: DISPLAY, fontSize: 20, fontWeight: 300, letterSpacing: '0.06em', marginTop: 6 },
  modalHelp: { fontSize: 13.5, color: 'var(--ib-mid)', lineHeight: 1.55, marginBottom: 4 },
  fieldLabel: { display: 'flex', alignItems: 'center', gap: 8, fontFamily: DISPLAY, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ib-muted)', margin: '20px 0 8px' },
  optional: { fontFamily: FONT, fontSize: 11, letterSpacing: 0, textTransform: 'none', color: 'var(--ib-muted)', fontWeight: 400 },
  fieldErr: { fontSize: 12, color: HEART, marginTop: 6 },
  popTitle: { fontFamily: DISPLAY, fontSize: 10, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ib-muted)', marginBottom: 10 },
  popBody: { fontSize: 13.5, lineHeight: 1.5, color: 'var(--ib-mid)' },
  popActions: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 14 },
  comboEmpty: { padding: '14px 10px', fontSize: 13, color: 'var(--ib-muted)', textAlign: 'center' },
  sideTop: { display: 'flex', alignItems: 'center', gap: 8 },
  byline: { display: 'flex', alignItems: 'center', gap: 11, marginTop: 20 },
  bylineName: { fontSize: 14, fontWeight: 600 },
  bylineSub: { fontSize: 12, color: 'var(--ib-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  savedChip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: FONT, fontSize: 11, letterSpacing: 0, textTransform: 'none', color: '#2f7d6b', fontWeight: 500 },
  readNote: { fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' },
  commentsHead: { display: 'flex', alignItems: 'center', gap: 8, fontFamily: DISPLAY, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ib-muted)', margin: '26px 0 6px' },
  commentCount: { fontFamily: MONO, fontSize: 10.5, letterSpacing: 0, background: 'var(--ib-subtle)', color: 'var(--ib-mid)', borderRadius: 999, padding: '1px 7px' },
  comments: { display: 'flex', flexDirection: 'column', gap: 2, margin: '0 -10px 10px' },
  noComments: { fontSize: 13, color: 'var(--ib-muted)', padding: '6px 10px 4px', lineHeight: 1.5 },
  commentHead: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 2 },
  commentBody: { fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', color: 'var(--ib-text)', overflowWrap: 'anywhere' },
  internalChip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', color: '#8a6a35', background: 'rgba(176,141,87,.18)', borderRadius: 999, padding: '2px 8px' },
  composerBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '4px 8px 8px 10px' },
  composerHint: { fontSize: 11, color: 'var(--ib-muted)' },
}
